/**
 * PhonePrefixInput — Phase 39 (libphonenumber-js).
 *
 * Combo prefisso (tutti i paesi via libphonenumber-js) + numero.
 * Mobile-first, dropdown ricercabile, default +41.
 *
 * Props:
 *   prefix (string) — "+41" | "+39" | "+33" ...
 *   onPrefixChange(string)
 *   number (string)
 *   onNumberChange(string)
 *   label, testidPrefix, required, placeholder
 */
import { useState, useMemo, useEffect, useRef } from "react";
import { ChevronDown, Search } from "lucide-react";
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from "libphonenumber-js";

// Localized country names (italiano). Per i paesi non in lista, fallback al codice ISO.
// Lista ridotta — copre la maggior parte dei casi. libphonenumber-js fornisce
// i prefissi (+41 ecc) per tutti i ~250 country code.
const COUNTRY_LABEL_IT = {
  CH: "Svizzera", IT: "Italia", DE: "Germania", FR: "Francia", AT: "Austria",
  ES: "Spagna", PT: "Portogallo", GB: "Regno Unito", IE: "Irlanda",
  NL: "Paesi Bassi", BE: "Belgio", LU: "Lussemburgo", DK: "Danimarca",
  SE: "Svezia", NO: "Norvegia", FI: "Finlandia", PL: "Polonia",
  CZ: "Repubblica Ceca", SK: "Slovacchia", HU: "Ungheria", RO: "Romania",
  BG: "Bulgaria", GR: "Grecia", HR: "Croazia", SI: "Slovenia",
  RS: "Serbia", BA: "Bosnia ed Erzegovina", AL: "Albania", MK: "Macedonia del Nord",
  ME: "Montenegro", TR: "Turchia", RU: "Russia", UA: "Ucraina",
  US: "Stati Uniti", CA: "Canada", MX: "Messico", BR: "Brasile",
  AR: "Argentina", CL: "Cile", CO: "Colombia", PE: "Perù",
  CN: "Cina", JP: "Giappone", KR: "Corea del Sud", IN: "India",
  AU: "Australia", NZ: "Nuova Zelanda", ZA: "Sudafrica", EG: "Egitto",
  MA: "Marocco", TN: "Tunisia", AE: "Emirati Arabi Uniti", SA: "Arabia Saudita",
  IL: "Israele", LI: "Liechtenstein", MC: "Monaco", SM: "San Marino", VA: "Città del Vaticano",
};

// Emoji flag dalla coppia di lettere ISO
function flagEmoji(iso) {
  if (!iso || iso.length !== 2) return "🌍";
  const codePoints = iso.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

// PRIORITARI in testa (Svizzera, Italia, vicini)
const PRIORITY = ["CH", "IT", "DE", "FR", "AT", "GB", "ES", "PT", "US"];

function buildOptions() {
  const all = getCountries();
  const seen = new Set();
  const items = [];
  for (const iso of PRIORITY) {
    if (all.includes(iso)) {
      seen.add(iso);
      items.push({
        iso,
        callingCode: `+${getCountryCallingCode(iso)}`,
        label: COUNTRY_LABEL_IT[iso] || iso,
        flag: flagEmoji(iso),
        priority: true,
      });
    }
  }
  // Resto, ordine alfabetico per nome
  const rest = all
    .filter((iso) => !seen.has(iso))
    .map((iso) => ({
      iso,
      callingCode: `+${getCountryCallingCode(iso)}`,
      label: COUNTRY_LABEL_IT[iso] || iso,
      flag: flagEmoji(iso),
      priority: false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "it"));
  return [...items, ...rest];
}

const ALL_OPTIONS = buildOptions();

function findByPrefix(prefix) {
  if (!prefix) return ALL_OPTIONS.find((o) => o.iso === "CH");
  // Match esatto sul calling code (priorità a Svizzera per +41)
  return (
    ALL_OPTIONS.find((o) => o.callingCode === prefix && o.priority) ||
    ALL_OPTIONS.find((o) => o.callingCode === prefix) ||
    ALL_OPTIONS.find((o) => o.iso === "CH")
  );
}

export default function PhonePrefixInput({
  prefix = "+41",
  onPrefixChange,
  number = "",
  onNumberChange,
  label = "Telefono",
  testidPrefix = "phone",
  required = false,
  placeholder = "76 123 45 67",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const dropdownRef = useRef(null);
  const current = useMemo(() => findByPrefix(prefix), [prefix]);

  // Validation leggera: se number c'è, prova a validare. Niente errore se vuoto.
  const fullNumber = `${prefix || "+41"}${(number || "").replace(/\s/g, "")}`;
  const isValid = !number || number.length < 4 || isValidPhoneNumber(fullNumber);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_OPTIONS;
    return ALL_OPTIONS.filter((o) =>
      o.label.toLowerCase().includes(q) ||
      o.iso.toLowerCase().includes(q) ||
      o.callingCode.includes(q)
    );
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const select = (opt) => {
    onPrefixChange?.(opt.callingCode);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="min-w-0">
      <label className="text-xs font-semibold tracking-tight">
        {label}{required && typeof label === "string" && " *"}
      </label>
      <div className="mt-1 flex gap-2 relative min-w-0" ref={dropdownRef}>
        <button
          type="button"
          data-testid={`${testidPrefix}-prefix`}
          onClick={() => setOpen((v) => !v)}
          className="input-tall flex items-center gap-1.5 px-2.5 min-w-[96px] sm:min-w-[110px] flex-shrink-0 hover:border-dapnow-blue/40 transition"
        >
          <span className="text-base">{current?.flag}</span>
          <span className="font-semibold text-sm">{current?.callingCode || "+41"}</span>
          <ChevronDown className="h-3.5 w-3.5 text-dapnow-muted ml-auto" />
        </button>
        <input
          data-testid={`${testidPrefix}-number`}
          className={`input-tall flex-1 min-w-0 w-full ${!isValid ? "border-dapnow-red focus:border-dapnow-red" : ""}`}
          type="tel"
          inputMode="tel"
          value={number || ""}
          onChange={(e) => onNumberChange?.(e.target.value)}
          placeholder={placeholder}
        />

        {open && (
          <div
            className="absolute left-0 top-full mt-1 z-50 w-72 sm:w-80 max-h-72 overflow-hidden bg-white rounded-2xl shadow-xl border border-dapnow-line"
            data-testid={`${testidPrefix}-prefix-dropdown`}
          >
            <div className="p-2 border-b border-dapnow-line">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-dapnow-muted" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Cerca paese o prefisso…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid={`${testidPrefix}-prefix-search`}
                  className="w-full h-9 rounded-lg border border-dapnow-line pl-8 pr-2 text-sm focus:border-dapnow-blue focus:outline-none"
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-56">
              {filtered.length === 0 ? (
                <p className="text-xs text-dapnow-muted text-center py-4">Nessun risultato.</p>
              ) : (
                filtered.map((opt) => (
                  <button
                    key={opt.iso}
                    type="button"
                    onClick={() => select(opt)}
                    data-testid={`${testidPrefix}-prefix-opt-${opt.iso}`}
                    className={`w-full text-left px-3 py-2 hover:bg-dapnow-bg flex items-center gap-2.5 text-sm ${opt.priority ? "bg-dapnow-blue-soft/40" : ""}`}
                  >
                    <span className="text-base">{opt.flag}</span>
                    <span className="flex-1 truncate">{opt.label}</span>
                    <span className="font-mono text-xs text-dapnow-muted">{opt.callingCode}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {!isValid && number && number.length >= 4 && (
        <p className="text-xs text-dapnow-red mt-1" data-testid={`${testidPrefix}-invalid`}>
          Numero non valido per {current?.label || "questo paese"}.
        </p>
      )}
    </div>
  );
}
